import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { RoadTo2027Badge, CopfApprovedBadge } from "@/components/MemoryBookStatusBadge";

/**
 * Rendu web "magazine" d'un livret AITO — aperçu avant PDF.
 * Ne cherche pas à imiter 100% le PDF ; vise cohérence de marque + hiérarchie.
 */
export const MemoryBookViewer = ({ version, snapshot }) => {
  if (!version) {
    return (
      <div className="text-sm text-muted-foreground italic" data-testid="memorybook-viewer-empty">
        Aucune version générée pour l'instant.
      </div>
    );
  }
  const entity = snapshot?.entity_data || {};
  const sections = (version.sections || [])
    .filter((s) => s.visible !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const figures = snapshot?.key_figures || {};
  const badges = version.badges || {};

  return (
    <div className="space-y-6" data-testid="memorybook-viewer">
      {/* Cover — simulation */}
      <div className="relative overflow-hidden rounded-lg border">
        <div className="aspect-[3/2] bg-gradient-to-br from-[hsl(var(--aito-black))/0.9] to-[hsl(var(--aito-lagoon))/0.4]">
          {(version.cover_photo_url || entity.photo_url) && (
            <img
              src={version.cover_photo_url || entity.photo_url}
              alt={entity.full_name || ""}
              className="h-full w-full object-cover object-top mix-blend-luminosity opacity-90"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="h-16 w-0.5 bg-[hsl(var(--aito-gold))]" />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/80">
                  {(entity.main_discipline || "").toUpperCase()}
                </div>
                <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
                  {version.title || entity.full_name}
                </h1>
                <p className="text-sm text-white/85 mt-1">{version.subtitle || "Portrait / Bilan AITO.PF"}</p>
              </div>
            </div>
          </div>
          <div className="absolute top-4 right-4 flex flex-col items-end gap-2">
            {badges.road_to_2027 && <RoadTo2027Badge />}
            {badges.copf_approved && <CopfApprovedBadge />}
          </div>
          <div className="absolute top-4 left-4 text-white text-xs font-mono">AITO.PF</div>
        </div>
      </div>

      {/* Sections */}
      {sections.map((sec) => (
        <Card key={sec.key} data-testid={`viewer-section-${sec.key}`}>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-baseline gap-3">
              <div className="h-0.5 w-10 bg-[hsl(var(--aito-lagoon))]" />
              <h2 className="text-xl font-display font-semibold">{sec.title}</h2>
              {sec.subtitle && (
                <p className="text-xs text-muted-foreground">{sec.subtitle}</p>
              )}
            </div>

            {sec.kind === "figures" && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                {[
                  ["Compétitions", figures.competitions_count],
                  ["Résultats", figures.achievements_count],
                  ["Médailles", figures.medals_count],
                  ["Titres (Or)", figures.golds_count],
                  ["International", figures.international_count],
                  ["Publications", figures.articles_count],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md border p-3">
                    <div className="text-2xl font-mono font-bold text-foreground">{value ?? 0}</div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
                  </div>
                ))}
              </div>
            )}

            {sec.kind === "palmares" && (
              <>
                {sec.content_md && (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{sec.content_md}</p>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium">Année</th>
                        <th className="text-left py-2 font-medium">Compétition</th>
                        <th className="text-left py-2 font-medium">Rang / Médaille</th>
                        <th className="text-left py-2 font-medium">Niveau</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(snapshot?.achievements || []).slice(0, 15).map((a, i) => (
                        <tr key={i} className="border-b/50">
                          <td className="py-1.5 font-mono">{a.year || "—"}</td>
                          <td className="py-1.5">{a.competition || a.title}</td>
                          <td className="py-1.5">{(a.medal || a.place || "—")}</td>
                          <td className="py-1.5 capitalize">{a.level || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {sec.kind === "timeline" && (
              <ul className="space-y-2">
                {(snapshot?.timeline_items || []).slice(0, 15).map((it, i) => (
                  <li key={i} className="flex gap-3 border-l-2 border-[hsl(var(--aito-lagoon))] pl-3">
                    <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">
                      {(it.date || "").slice(0, 10)}
                    </span>
                    <span className="text-sm"><b>{it.title}</b>{it.detail ? ` — ${it.detail}` : ""}</span>
                  </li>
                ))}
              </ul>
            )}

            {sec.kind === "competitions" && (
              <div className="grid md:grid-cols-2 gap-3">
                {(snapshot?.competitions || []).slice(0, 6).map((c) => (
                  <div key={c.source_id} className="rounded-md border-t-2 border-[hsl(var(--aito-gold))] border-x border-b p-3">
                    <div className="font-medium text-sm">{c.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {(c.date_start || "").slice(0, 10)} • {c.location}
                    </div>
                    {c.level && <div className="text-[11px] font-medium capitalize mt-1">{c.level}</div>}
                  </div>
                ))}
              </div>
            )}

            {sec.kind === "articles" && (
              <ul className="space-y-3">
                {(snapshot?.articles || []).slice(0, 8).map((a) => (
                  <li key={a.source_id} className="text-sm">
                    <div className="font-medium">{a.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {a.source_name} • {(a.published_at || "").slice(0, 10)}
                    </div>
                    {a.summary && <p className="text-xs text-muted-foreground italic mt-1">{a.summary.slice(0, 220)}</p>}
                  </li>
                ))}
              </ul>
            )}

            {sec.kind === "road2027" && snapshot?.road_to_2027_data?.active && (
              <>
                <div className="grid md:grid-cols-2 gap-2 text-sm">
                  {snapshot.road_to_2027_data.objective && (
                    <div><b>Objectif :</b> {snapshot.road_to_2027_data.objective}</div>
                  )}
                  {snapshot.road_to_2027_data.discipline && (
                    <div><b>Discipline cible :</b> {snapshot.road_to_2027_data.discipline}</div>
                  )}
                  {snapshot.road_to_2027_data.selection_status && (
                    <div><b>Statut sélection :</b> {snapshot.road_to_2027_data.selection_status}</div>
                  )}
                  {snapshot.road_to_2027_data.preparation_status && (
                    <div><b>Statut préparation :</b> {snapshot.road_to_2027_data.preparation_status}</div>
                  )}
                </div>
                {sec.content_md && (
                  <>
                    <Separator className="my-3" />
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{sec.content_md}</p>
                  </>
                )}
              </>
            )}

            {sec.kind === "sources" && (
              <div className="text-xs font-mono text-muted-foreground space-y-1">
                {Object.entries(snapshot?.source_ids || {}).map(([k, v]) => (
                  (v || []).length > 0 && (
                    <div key={k}>
                      <b className="uppercase">{k}</b> : {v.length} élément(s)
                    </div>
                  )
                ))}
              </div>
            )}

            {sec.kind === "edito" && sec.content_md && (
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{sec.content_md}</p>
            )}

            {!(["edito", "figures", "palmares", "timeline", "competitions", "articles", "road2027", "sources"].includes(sec.kind)) && sec.content_md && (
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{sec.content_md}</p>
            )}
          </CardContent>
        </Card>
      ))}

      <div className="text-xs font-mono text-muted-foreground pt-4 border-t">
        AITO.PF · v{version.version_number || 1} · {version.finalized_at ? `finalisé le ${new Date(version.finalized_at).toLocaleDateString("fr-FR")}` : "brouillon"}
      </div>
    </div>
  );
};
